import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiStar, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { FaQuoteLeft } from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';
import ncb from '../db/ncb';

const ReviewsCarousel = () => {
  const [reviews, setReviews] = useState([]);
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  useEffect(() => {
    const data = ncb.reviews.list();
    // Filter to only show featured reviews on the frontend
    setReviews(data.filter(r => r.is_featured));
  }, []);

  useEffect(() => {
    if (reviews.length === 0) return;
    const timer = setInterval(() => {
      nextStep();
    }, 10000);
    return () => clearInterval(timer);
  }, [index, reviews]);

  const nextStep = () => {
    if (reviews.length === 0) return;
    setDirection(1);
    setIndex((prev) => (prev + 1) % reviews.length);
  };

  const prevStep = () => {
    if (reviews.length === 0) return;
    setDirection(-1);
    setIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
  };

  const variants = {
    enter: (direction) => ({ x: direction > 0 ? 100 : -100, opacity: 0, scale: 0.95 }),
    center: { zIndex: 1, x: 0, opacity: 1, scale: 1 },
    exit: (direction) => ({ zIndex: 0, x: direction < 0 ? 100 : -100, opacity: 0, scale: 0.95 })
  };

  if (reviews.length === 0) return null;

  return (
    <div className="relative max-w-4xl mx-auto px-4 py-8">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 text-brand-olive/5">
        <SafeIcon icon={FaQuoteLeft} className="text-[12rem]" />
      </div>

      <div className="relative h-[680px] md:h-[480px] overflow-hidden">
        <AnimatePresence initial={false} custom={direction}>
          <motion.div 
            key={index} 
            custom={direction} 
            variants={variants} 
            initial="enter" 
            animate="center" 
            exit="exit" 
            transition={{ x: { type: "spring", stiffness: 300, damping: 30 }, opacity: { duration: 0.4 }, scale: { duration: 0.4 } }} 
            className="absolute inset-0 flex flex-col items-center text-center px-4 md:px-12" 
          >
            <div className="relative mb-8 pt-4">
              <div className="w-24 h-24 rounded-full border-4 border-brand-cream overflow-hidden shadow-xl ring-2 ring-brand-olive/20 bg-white">
                <img src={reviews[index].image_url || reviews[index].image} alt={reviews[index].guest_name || reviews[index].name} className="w-full h-full object-cover" />
              </div>
              <div className="absolute bottom-0 right-0 bg-brand-olive text-white w-8 h-8 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                <SafeIcon icon={FiStar} className="text-xs fill-current" />
              </div>
            </div>

            <div className="flex gap-1 mb-6 text-brand-olive">
              {[...Array(reviews[index].rating)].map((_, i) => (
                <SafeIcon key={i} icon={FiStar} className="fill-current text-sm" />
              ))}
            </div>

            <div className="flex-grow flex items-center justify-center mb-8">
              <p className="font-serif text-base md:text-xl text-brand-dark italic leading-relaxed max-w-2xl text-balance">
                "{reviews[index].content}"
              </p>
            </div>

            <div className="pb-6">
              <h4 className="font-bold text-brand-olive uppercase tracking-[0.2em] text-sm md:text-base">
                {reviews[index].guest_name || reviews[index].name}
              </h4>
              <p className="text-brand-sage text-xs uppercase tracking-widest mt-2 border-t border-brand-olive/10 pt-2 inline-block">
                {reviews[index].role}
              </p>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex justify-center items-center gap-8 mt-4 relative z-20">
        <button onClick={prevStep} className="p-3 rounded-full border border-brand-light/50 text-brand-sage hover:bg-brand-olive hover:text-brand-cream transition-all duration-300 group">
          <SafeIcon icon={FiChevronLeft} className="text-xl transform group-hover:-translate-x-1 transition-transform" />
        </button>
        <div className="flex gap-3">
          {reviews.map((_, i) => (
            <button key={i} onClick={() => { setDirection(i > index ? 1 : -1); setIndex(i); }} className={`h-1.5 rounded-full transition-all duration-500 ${i === index ? 'w-10 bg-brand-olive shadow-sm' : 'w-2 bg-brand-light/50'}`} />
          ))}
        </div>
        <button onClick={nextStep} className="p-3 rounded-full border border-brand-light/50 text-brand-sage hover:bg-brand-olive hover:text-brand-cream transition-all duration-300 group">
          <SafeIcon icon={FiChevronRight} className="text-xl transform group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </div>
  );
};

export default ReviewsCarousel;